import React, { useState } from "react"
import { ArrowLeft, Check, Loader2, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { AuthLoginSubmit } from "@/components/auth-login-submit"
import {
  AuthMirroredSplitShell,
  AuthSplitSwitchLink,
} from "@/components/auth-mirrored-split-shell"
import {
  AuthSwipePanel,
  AuthSwipeTrack,
  AuthSwipeViewport,
} from "@/components/auth-swipe-panels"
import { AuthOAuthButtons, type OAuthStrategy } from "@/components/auth-oauth-buttons"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { MosaicOtpInput } from "@/components/mosaic-otp-input"
import {
  authSplitCardClass,
  authSplitCardShellClass,
  authSplitFormClass,
  authSplitFormCenteredClass,
  authWelcomeTitleClass,
  AUTH_MUTED_TEXT_CLASS,
} from "@/components/auth-split-card-layout"
import {
  confirmPasswordFieldState,
  EMAIL_MAX_LENGTH,
  fieldStateFromValue,
  getPasswordRequirementFlags,
  isValidEmail,
  isValidPersonName,
  isValidUsername,
  PASSWORD_MAX_LENGTH,
  passwordFieldState,
  passwordsMatch,
  PERSON_NAME_MAX_LENGTH,
  type AuthFieldState,
  USERNAME_MAX_LENGTH,
} from "@/utils/authFieldValidation"
import {
  PNL_NEGATIVE_COLOR,
  PNL_POSITIVE_COLOR,
} from "@/views/Dashboard/Statistics/components/statsSignedPnlBarUtils"
import { useUsernameAvailability } from "@/hooks/useUsernameAvailability"

export type SignupFormProps = {
  className?: string
  pendingVerification: boolean
  email: string
  firstName: string
  lastName: string
  username: string
  password: string
  confirmPassword: string
  verificationCode: string
  marketingOptIn: boolean
  termsAccepted: boolean
  loading: boolean
  /** Email verification accepted — play mosaic morph before redirect. */
  verificationSuccess?: boolean
  onVerificationSuccessComplete?: () => void
  /** Demo Entry Analysis: report is cooking; signup unlocks results. */
  demoEntryAnalysisGate?: boolean
  oauthLoading: OAuthStrategy | null
  error: string
  resendNotice?: string
  codeInputInvalid?: boolean
  onEmailChange: (value: string) => void
  onFirstNameChange: (value: string) => void
  onLastNameChange: (value: string) => void
  onUsernameChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onConfirmPasswordChange: (value: string) => void
  onVerificationCodeChange: (value: string) => void
  onMarketingOptInChange: (checked: boolean) => void
  onTermsAcceptedChange: (checked: boolean) => void
  onSubmit: (e: React.FormEvent) => void
  onVerificationSubmit: (e: React.FormEvent) => void
  onResendCode: () => void
  onAbandonVerification: () => void
  onOAuthSignUp: (strategy: OAuthStrategy) => void
}

const AUTH_FONT_PRIMARY = "font-[family-name:var(--font-primary)]"
const AUTH_INPUT_CLASS = cn(
  AUTH_FONT_PRIMARY,
  "h-8 text-sm text-foreground dark:!text-white dark:placeholder:!text-white/50"
)
const AUTH_TROPICAL_SURF_TEXT = "text-[color:var(--brand-tropical-surf)]"

const AUTH_FIELD_LABEL_CLASS = cn(
  "font-medium",
  AUTH_FONT_PRIMARY,
  AUTH_TROPICAL_SURF_TEXT
)

function authInputClass(state: AuthFieldState) {
  return cn(
    AUTH_INPUT_CLASS,
    state === "neutral" && "!border-border dark:!border-border",
    state === "valid" &&
      "focus-visible:!ring-[color:var(--auth-field-stroke)]/30",
    state === "invalid" &&
      "focus-visible:!ring-[color:var(--auth-field-stroke)]/25"
  )
}

function authInputStyle(state: AuthFieldState): React.CSSProperties | undefined {
  if (state === "valid") {
    return {
      borderColor: PNL_POSITIVE_COLOR,
      ["--auth-field-stroke" as string]: PNL_POSITIVE_COLOR,
    }
  }
  if (state === "invalid") {
    return {
      borderColor: PNL_NEGATIVE_COLOR,
      ["--auth-field-stroke" as string]: PNL_NEGATIVE_COLOR,
    }
  }
  return undefined
}

function AuthRequiredMark() {
  return (
    <span className="inline text-destructive" aria-hidden="true">
      {" "}
      *
    </span>
  )
}

function AuthFieldLabel({
  htmlFor,
  children,
  className = AUTH_FIELD_LABEL_CLASS,
  required = true,
}: {
  htmlFor: string
  children: React.ReactNode
  className?: string
  required?: boolean
}) {
  return (
    <FieldLabel htmlFor={htmlFor} className={className}>
      {children}
      {required ? <AuthRequiredMark /> : null}
    </FieldLabel>
  )
}

function FormAlert({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-auth-form-message
      role="alert"
      className={cn(
        "w-full rounded-[6px] border border-destructive/40 bg-destructive/10 px-3.5 py-3",
        "text-center text-sm leading-relaxed text-destructive",
        AUTH_FONT_PRIMARY
      )}
    >
      {children}
    </div>
  )
}

function UsernameAvailabilityMessage({
  status,
}: {
  status: ReturnType<typeof useUsernameAvailability>
}) {
  if (status === "idle") return null

  if (status === "checking") {
    return (
      <p
        data-slot="field-description"
        data-auth-username-availability="checking"
        className={cn("!mt-1 flex items-center gap-1.5 text-sm leading-normal", AUTH_FONT_PRIMARY)}
      >
        Checking availability…
      </p>
    )
  }

  if (status === "available" || status === "taken") {
    const isAvailable = status === "available"
    const color = isAvailable ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR
    return (
      <p
        data-slot="field-description"
        data-auth-username-availability={status}
        className={cn(
          "!mt-1 flex items-center gap-1.5 text-sm leading-normal font-normal",
          AUTH_FONT_PRIMARY
        )}
        style={{ color }}
      >
        <span>{isAvailable ? "Username is available" : "Username is taken"}</span>
        <span
          data-auth-availability-icon=""
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-full"
          style={{
            boxSizing: "border-box",
            borderRadius: "9999px",
            borderWidth: "1.5px",
            borderStyle: "solid",
            borderColor: color,
            color,
          }}
          aria-hidden
        >
          {isAvailable ? (
            <Check className="size-2.5" strokeWidth={3} />
          ) : (
            <X className="size-2.5" strokeWidth={3} />
          )}
        </span>
      </p>
    )
  }

  return (
    <p
      data-slot="field-description"
      data-auth-username-availability={status}
      className={cn("!mt-1 text-sm leading-normal", AUTH_FONT_PRIMARY)}
    >
      {status === "invalid"
        ? "Username must be 3–64 characters (letters, numbers, or underscores)."
        : "Couldn’t check availability right now. Wait a moment and try again."}
    </p>
  )
}

function PasswordRequirementItem({ met, label }: { met: boolean; label: string }) {
  return (
    <li
      className={cn(
        "flex items-center gap-2 text-xs",
        !met && "text-muted-foreground dark:!text-white/70"
      )}
      style={met ? { color: PNL_POSITIVE_COLOR } : undefined}
    >
      <span
        className={cn(
          "inline-flex size-1.5 shrink-0 rounded-full",
          !met && "bg-muted-foreground/40 dark:bg-white/30"
        )}
        style={met ? { backgroundColor: PNL_POSITIVE_COLOR } : undefined}
        aria-hidden
      />
      {label}
    </li>
  )
}

export function SignupForm({
  className,
  pendingVerification,
  email,
  firstName,
  lastName,
  username,
  password,
  confirmPassword,
  verificationCode,
  marketingOptIn,
  termsAccepted,
  loading,
  verificationSuccess = false,
  onVerificationSuccessComplete,
  demoEntryAnalysisGate = false,
  oauthLoading,
  error,
  resendNotice,
  codeInputInvalid = false,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onUsernameChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onVerificationCodeChange,
  onMarketingOptInChange,
  onTermsAcceptedChange,
  onSubmit,
  onVerificationSubmit,
  onResendCode,
  onAbandonVerification,
  onOAuthSignUp,
}: SignupFormProps) {
  const oauthBusy = oauthLoading !== null
  const signUpBusy = loading || oauthBusy || verificationSuccess || pendingVerification
  const verifyChromeBusy = loading || verificationSuccess
  const usernameAvailability = useUsernameAvailability(username)

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    username: false,
    email: false,
    password: false,
    confirmPassword: false,
  })

  const markTouched = (field: keyof typeof touched) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }))
  }

  const firstNameState = fieldStateFromValue(firstName, isValidPersonName, touched.firstName)
  const lastNameState = fieldStateFromValue(lastName, isValidPersonName, touched.lastName)
  const usernameFormatState = fieldStateFromValue(username, isValidUsername, touched.username)
  const usernameState: AuthFieldState =
    usernameFormatState === "valid" && usernameAvailability === "taken"
      ? "invalid"
      : usernameFormatState === "valid" && usernameAvailability === "available"
        ? "valid"
        : usernameFormatState
  const emailState = fieldStateFromValue(email, isValidEmail, touched.email)
  const passwordState = passwordFieldState(password, touched.password)
  const confirmState = confirmPasswordFieldState(
    password,
    confirmPassword,
    touched.confirmPassword
  )

  const passwordFlags = getPasswordRequirementFlags(password)
  const passwordsDoMatch = passwordsMatch(password, confirmPassword)
  const showPasswordChecklist = password.length > 0 || confirmPassword.length > 0

  return (
    <div
      data-auth-login-form
      data-auth-signup-form
      data-auth-verify-step={pendingVerification ? "" : undefined}
      className={authSplitCardShellClass(cn(AUTH_FONT_PRIMARY, className))}
    >
      <Card className={authSplitCardClass}>
        <AuthMirroredSplitShell variant="signup">
          <AuthSwipeViewport>
            <AuthSwipeTrack offset={pendingVerification ? 1 : 0}>
              <AuthSwipePanel active={!pendingVerification}>
                <form className={authSplitFormClass} onSubmit={onSubmit}>
                  <FieldGroup className="flex flex-1 flex-col">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <h1 data-auth-welcome-title className={authWelcomeTitleClass}>
                        {demoEntryAnalysisGate
                          ? "Sign up to see your results"
                          : "Create your account"}
                      </h1>
                      <p className="text-sm text-balance text-muted-foreground dark:!text-white">
                        {demoEntryAnalysisGate
                          ? "Your report is ready for review. Create a free account to unlock it."
                          : "Enter your details below to create your account"}
                      </p>
                    </div>
                    {error && !pendingVerification ? <FormAlert>{error}</FormAlert> : null}
                    <Field className="grid grid-cols-2 gap-4">
                      <Field>
                        <AuthFieldLabel htmlFor="first-name">First name</AuthFieldLabel>
                        <Input
                          id="first-name"
                          type="text"
                          value={firstName}
                          onChange={(e) => onFirstNameChange(e.target.value)}
                          onBlur={() => markTouched("firstName")}
                          placeholder="First name"
                          disabled={signUpBusy}
                          autoComplete="given-name"
                          maxLength={PERSON_NAME_MAX_LENGTH}
                          required
                          aria-invalid={firstNameState === "invalid" || undefined}
                          data-auth-field-state={firstNameState}
                          className={authInputClass(firstNameState)}
                          style={authInputStyle(firstNameState)}
                        />
                      </Field>
                      <Field>
                        <AuthFieldLabel htmlFor="last-name">Last name</AuthFieldLabel>
                        <Input
                          id="last-name"
                          type="text"
                          value={lastName}
                          onChange={(e) => onLastNameChange(e.target.value)}
                          onBlur={() => markTouched("lastName")}
                          placeholder="Last name"
                          disabled={signUpBusy}
                          autoComplete="family-name"
                          maxLength={PERSON_NAME_MAX_LENGTH}
                          required
                          aria-invalid={lastNameState === "invalid" || undefined}
                          data-auth-field-state={lastNameState}
                          className={authInputClass(lastNameState)}
                          style={authInputStyle(lastNameState)}
                        />
                      </Field>
                    </Field>
                    <Field>
                      <AuthFieldLabel htmlFor="username">Username</AuthFieldLabel>
                      <Input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => onUsernameChange(e.target.value)}
                        onBlur={() => markTouched("username")}
                        placeholder="Username"
                        disabled={signUpBusy}
                        autoComplete="username"
                        maxLength={USERNAME_MAX_LENGTH}
                        spellCheck={false}
                        autoCapitalize="off"
                        required
                        aria-invalid={usernameState === "invalid" || undefined}
                        data-auth-field-state={usernameState}
                        className={authInputClass(usernameState)}
                        style={authInputStyle(usernameState)}
                      />
                      {username.length > 0 ? (
                        <UsernameAvailabilityMessage status={usernameAvailability} />
                      ) : null}
                    </Field>
                    <Field>
                      <AuthFieldLabel htmlFor="email">Email</AuthFieldLabel>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => onEmailChange(e.target.value)}
                        onBlur={() => markTouched("email")}
                        placeholder="tes@example.com"
                        disabled={signUpBusy}
                        autoComplete="email"
                        maxLength={EMAIL_MAX_LENGTH}
                        required
                        aria-invalid={emailState === "invalid" || undefined}
                        data-auth-field-state={emailState}
                        className={authInputClass(emailState)}
                        style={authInputStyle(emailState)}
                      />
                    </Field>
                    <Field>
                      <Field className="grid grid-cols-2 gap-4">
                        <Field>
                          <AuthFieldLabel htmlFor="password">Password</AuthFieldLabel>
                          <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => onPasswordChange(e.target.value)}
                            onBlur={() => markTouched("password")}
                            disabled={signUpBusy}
                            autoComplete="new-password"
                            placeholder="Password"
                            maxLength={PASSWORD_MAX_LENGTH}
                            required
                            aria-invalid={passwordState === "invalid" || undefined}
                            data-auth-field-state={passwordState}
                            className={authInputClass(passwordState)}
                            style={authInputStyle(passwordState)}
                          />
                        </Field>
                        <Field>
                          <AuthFieldLabel htmlFor="confirm-password">Confirm password</AuthFieldLabel>
                          <Input
                            id="confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => onConfirmPasswordChange(e.target.value)}
                            onBlur={() => markTouched("confirmPassword")}
                            disabled={signUpBusy}
                            autoComplete="new-password"
                            placeholder="Confirm password"
                            maxLength={PASSWORD_MAX_LENGTH}
                            required
                            aria-invalid={confirmState === "invalid" || undefined}
                            data-auth-field-state={confirmState}
                            className={authInputClass(confirmState)}
                            style={authInputStyle(confirmState)}
                          />
                        </Field>
                      </Field>
                      {showPasswordChecklist ? (
                        <ul
                          className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1"
                          aria-live="polite"
                        >
                          <PasswordRequirementItem
                            met={passwordFlags.minLength}
                            label="At least 8 characters"
                          />
                          <PasswordRequirementItem
                            met={passwordFlags.hasLetter}
                            label="Contains a letter"
                          />
                          <PasswordRequirementItem
                            met={passwordFlags.hasNumber}
                            label="Contains a number"
                          />
                          <PasswordRequirementItem
                            met={passwordFlags.hasSymbol}
                            label="Contains a symbol"
                          />
                          {confirmPassword.length > 0 ? (
                            <PasswordRequirementItem
                              met={passwordsDoMatch}
                              label="Passwords match"
                            />
                          ) : null}
                        </ul>
                      ) : (
                        <FieldDescription>
                          Must be at least 8 characters and include a letter, a number, and a symbol.
                        </FieldDescription>
                      )}
                    </Field>
                    <Field orientation="horizontal" className="items-start gap-2">
                      <Checkbox
                        id="marketing-opt-in"
                        data-auth-checkbox=""
                        checked={marketingOptIn}
                        onCheckedChange={(checked) => onMarketingOptInChange(checked === true)}
                        disabled={signUpBusy}
                      />
                      <FieldLabel
                        htmlFor="marketing-opt-in"
                        className={cn(
                          "!block min-w-0 flex-1 font-normal text-muted-foreground dark:!text-white",
                          AUTH_FONT_PRIMARY
                        )}
                      >
                        Email me about new features, product updates and offers from Tessera Trades
                      </FieldLabel>
                    </Field>
                    <Field orientation="horizontal" className="items-start gap-2">
                      <Checkbox
                        id="terms-accepted"
                        data-auth-checkbox=""
                        checked={termsAccepted}
                        onCheckedChange={(checked) => onTermsAcceptedChange(checked === true)}
                        disabled={signUpBusy}
                        required
                      />
                      <FieldLabel
                        htmlFor="terms-accepted"
                        className={cn(
                          "!block min-w-0 flex-1 font-normal leading-snug text-muted-foreground dark:!text-white",
                          AUTH_FONT_PRIMARY
                        )}
                      >
                        I have read and agree to the{" "}
                        <a
                          href="/terms"
                          data-auth-text-link-muted
                          className={cn("inline whitespace-nowrap hover:opacity-90", AUTH_MUTED_TEXT_CLASS)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://tesseratrades.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-auth-text-link-muted
                          className={cn("inline whitespace-nowrap hover:opacity-90", AUTH_MUTED_TEXT_CLASS)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </a>
                        .
                        <AuthRequiredMark />
                      </FieldLabel>
                    </Field>
                    <Field>
                      {/* Required for Clerk Bot sign-up protection on custom flows.
                          Without this node, invisible CAPTCHA can stall Create account. */}
                      <div id="clerk-captcha" className="min-h-0" />
                      <AuthLoginSubmit loading={loading} disabled={oauthBusy || !termsAccepted}>
                        {loading ? (
                          <>
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                            Creating account...
                          </>
                        ) : (
                          "Create account"
                        )}
                      </AuthLoginSubmit>
                    </Field>
                    <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card dark:*:data-[slot=field-separator-content]:bg-sidebar">
                      Or continue with
                    </FieldSeparator>
                    <AuthOAuthButtons
                      action="Sign up"
                      oauthLoading={oauthLoading}
                      busy={signUpBusy}
                      onOAuth={onOAuthSignUp}
                    />
                    <FieldDescription className="text-center">
                      Already have an account?{" "}
                      <AuthSplitSwitchLink
                        to="signin"
                        data-auth-text-link-signup
                        className={cn("inline hover:opacity-90", AUTH_TROPICAL_SURF_TEXT)}
                      >
                        Sign in
                      </AuthSplitSwitchLink>
                    </FieldDescription>
                  </FieldGroup>
                </form>
              </AuthSwipePanel>

              <AuthSwipePanel active={pendingVerification}>
                <form
                  className={cn(authSplitFormCenteredClass, "relative")}
                  onSubmit={onVerificationSubmit}
                  aria-busy={verificationSuccess || undefined}
                >
                  <button
                    type="button"
                    data-auth-back-to-signup
                    className={cn(
                      "absolute bottom-6 right-6 inline-flex items-center gap-1.5 text-sm underline-offset-4 hover:underline md:bottom-8 md:right-8",
                      AUTH_MUTED_TEXT_CLASS,
                      AUTH_FONT_PRIMARY,
                      verificationSuccess && "pointer-events-none opacity-0"
                    )}
                    onClick={onAbandonVerification}
                    disabled={verifyChromeBusy}
                  >
                    <ArrowLeft className="size-4 shrink-0" aria-hidden />
                    Go back
                  </button>
                  <FieldGroup className="mx-auto w-full max-w-md items-center text-center">
                    <div
                      className={cn(
                        "flex w-full flex-col items-center gap-2 text-center transition-opacity duration-300",
                        verificationSuccess && "pointer-events-none opacity-40"
                      )}
                    >
                      <h1 data-auth-welcome-title className={authWelcomeTitleClass}>
                        Verify your email
                      </h1>
                      <p className="text-sm text-balance text-muted-foreground dark:!text-white">
                        {email ? (
                          <>
                            We sent a verification code to{" "}
                            <span
                              data-auth-verify-email
                              className="font-medium text-[color:var(--brand-tropical-surf)]"
                            >
                              {email}
                            </span>
                            . Enter it below.
                          </>
                        ) : (
                          <>We sent a verification code to your email. Enter it below.</>
                        )}
                      </p>
                    </div>
                    {error && pendingVerification && !verificationSuccess ? (
                      <FormAlert>{error}</FormAlert>
                    ) : null}
                    {resendNotice && !error && !verificationSuccess ? (
                      <p
                        data-auth-resend-notice
                        className={cn(
                          "w-full text-center text-sm leading-relaxed",
                          AUTH_FONT_PRIMARY,
                          AUTH_MUTED_TEXT_CLASS
                        )}
                      >
                        {resendNotice}
                      </p>
                    ) : null}
                    <Field className="w-full items-center text-center">
                      <MosaicOtpInput
                        id="verification-code"
                        value={verificationCode}
                        onChange={onVerificationCodeChange}
                        disabled={verifyChromeBusy}
                        invalid={codeInputInvalid && !verificationSuccess}
                        success={verificationSuccess}
                        onSuccessAnimationComplete={onVerificationSuccessComplete}
                        autoFocus={pendingVerification}
                        aria-label="Email verification code"
                        className="mx-auto justify-center"
                      />
                    </Field>
                    <Field
                      className={cn(
                        "flex w-full flex-col items-center transition-opacity duration-300",
                        verificationSuccess && "pointer-events-none opacity-0"
                      )}
                    >
                      <AuthLoginSubmit loading={loading} fit>
                        {loading ? (
                          <>
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                            Verifying...
                          </>
                        ) : (
                          "Verify email"
                        )}
                      </AuthLoginSubmit>
                    </Field>
                    <FieldDescription
                      className={cn(
                        "w-full text-center transition-opacity duration-300",
                        verificationSuccess && "pointer-events-none opacity-0"
                      )}
                    >
                      Didn&apos;t receive the code?{" "}
                      <button
                        type="button"
                        data-auth-text-link-signup
                        data-auth-resend-code
                        className={cn(
                          "text-sm underline-offset-4 hover:underline",
                          AUTH_TROPICAL_SURF_TEXT,
                          AUTH_FONT_PRIMARY
                        )}
                        onClick={onResendCode}
                        disabled={verifyChromeBusy}
                      >
                        Resend code
                      </button>
                    </FieldDescription>
                  </FieldGroup>
                </form>
              </AuthSwipePanel>
            </AuthSwipeTrack>
          </AuthSwipeViewport>
        </AuthMirroredSplitShell>
      </Card>
    </div>
  )
}
